import React, { useState, useEffect } from 'react';
import { useBillingSettings } from '../../hooks/useBillingSettings';
import { useCredentials } from '../../hooks/useCredentials';
import { BillingSettings as BillingSettingsType } from '../../types/billing';
import { Credential } from '../../types/credentials';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import CredentialEditor from './CredentialEditor';
import CredentialsManager from './CredentialsManager';
import szamlazzLogo from '../../assets/szamlazzhu_logo.svg';
import billingoLogo from '../../assets/billingo_logo.svg';
import { 
  CreditCardIcon, 
  KeyIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const BillingSettings: React.FC = () => {
  const { user } = useAuth();
  const {
    billingSettings,
    loading,
    error,
    createBillingSettings,
    updateBillingSettings,
    validateAgentKey,
    getDefaultSettings
  } = useBillingSettings();

  const { credentials, loading: credentialsLoading } = useCredentials();
  const [selectedCredentialId, setSelectedCredentialId] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<'szamlazz' | 'billingo'>('szamlazz');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<BillingSettingsType>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [keyValidationResult, setKeyValidationResult] = useState<boolean | null>(null);
  const [showCredentialsManager, setShowCredentialsManager] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(true);

  // Szűrjük a számlázó credentials-eket
  const billingCredentials = credentials.filter(c => c.type === 'szamlazz' || c.type === 'billingo');

  useEffect(() => {
    if (billingSettings) {
      setFormData(billingSettings);
    } else if (user?.organizationId) {
      // Ha nincs beállítás, töltse be az alapértelmezetteket
      try {
        const defaultSettings = getDefaultSettings();
        // Agent kulcs üres - felhasználónak be kell állítania
        setFormData({
          ...defaultSettings,
          agentKey: ''
        });
      } catch (err) {
        console.error('Error loading default settings:', err);
      }
    }
  }, [billingSettings, user?.organizationId, getDefaultSettings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agentKey) {
      toast.error('Agent kulcs megadása kötelező!');
      return;
    }

    try {
      if (billingSettings) {
        await updateBillingSettings(formData);
        toast.success('Számlázási beállítások sikeresen frissítve!');
      } else {
        await createBillingSettings(formData as Omit<BillingSettingsType, 'id' | 'createdAt' | 'updatedAt'>);
        toast.success('Számlázási beállítások sikeresen létrehozva!');
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving billing settings:', err);
      toast.error('Hiba történt a beállítások mentése során!');
    }
  };

  const handleValidateKey = async () => {
    if (!formData.agentKey) {
      toast.error('Adja meg az Agent kulcsot!');
      return;
    }

    setIsValidating(true);
    setKeyValidationResult(null);

    try {
      const result = await validateAgentKey(formData.agentKey);
      setKeyValidationResult(result.valid);
      
      if (result.valid) {
        toast.success('Agent kulcs érvényes!');
      } else {
        toast.error(`Érvénytelen Agent kulcs: ${result.message}`);
      }
    } catch (err) {
      console.error('Error validating key:', err);
      toast.error('Hiba történt a validáció során!');
    } finally {
      setIsValidating(false);
    }
  };

  const handleInputChange = (field: keyof BillingSettingsType, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Ha az Agent kulcs változik, reseteljük a validáció eredményét
    if (field === 'agentKey') {
      setKeyValidationResult(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <CreditCardIcon className="h-6 w-6 text-primary-600 mr-3" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Számlázási beállítások</h3>
            <div className="bg-orange-100 dark:bg-orange-900/20 border border-orange-300 dark:border-orange-700 rounded-md px-3 py-1 mt-1 inline-block">
              <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">🧪 DEMO MÓD - Csak előnézeti számlák készülnek</p>
            </div>
          </div>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md transition-colors"
          >
            {billingSettings ? 'Beállítások szerkesztése' : 'Beállítások létrehozása'}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {!billingSettings && !isEditing && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4 mb-6">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" />
            <div className="ml-3">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Még nincsenek beállítva számlázási integrációk. Hozzon létre egyet a számla kiállítás automatizálásához.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Credentials Manager gomb */}
      <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            <InformationCircleIcon className="h-6 w-6 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Számlázó rendszer credentials</p>
              <p>
                A számlázó rendszer API kulcsai biztonságosan titkosítva vannak tárolva. 
                Válassz ki egy credential-t vagy hozz létre újat.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCredentialsManager(true)}
            className="ml-4 inline-flex items-center px-3 py-2 border border-blue-300 dark:border-blue-600 text-sm font-medium rounded-md text-blue-700 dark:text-blue-300 bg-white dark:bg-blue-900/30 hover:bg-blue-50 dark:hover:bg-blue-900/50 transition-colors"
          >
            <KeyIcon className="h-4 w-4 mr-2" />
            Credentials Manager
          </button>
        </div>
      </div>

      {/* Szolgáltató választás */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Számlázó szolgáltató
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setSelectedProvider('szamlazz')}
            className={`p-4 border-2 rounded-lg transition-colors ${
              selectedProvider === 'szamlazz'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <img src={szamlazzLogo} alt="Számlázz.hu" className="h-8 w-auto object-contain" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Számlázz.hu</span>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setSelectedProvider('billingo')}
            className={`p-4 border-2 rounded-lg transition-colors ${
              selectedProvider === 'billingo'
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <div className="flex flex-col items-center space-y-2">
              <img src={billingoLogo} alt="Billingo" className="h-8 w-auto object-contain" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">Billingo</span>
            </div>
          </button>
        </div>
      </div>

      {/* Számlázó Credentials kiválasztása */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {selectedProvider === 'szamlazz' ? 'Számlázz.hu' : 'Billingo'} credential
        </label>
        
        {billingCredentials.filter(c => c.type === selectedProvider).length === 0 ? (
          <div className="text-center py-8 bg-gray-50 dark:bg-gray-700/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
            <KeyIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              Nincs {selectedProvider === 'szamlazz' ? 'Számlázz.hu' : 'Billingo'} credential
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Hozz létre egy {selectedProvider === 'szamlazz' ? 'Számlázz.hu' : 'Billingo'} credential-t a Credentials Manager-ben.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setShowCredentialsManager(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Új credential létrehozása
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {billingCredentials.filter(c => c.type === selectedProvider).map((credential) => (
              <div
                key={credential.id}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  selectedCredentialId === credential.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
                onClick={() => setSelectedCredentialId(credential.id)}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="radio"
                    checked={selectedCredentialId === credential.id}
                    onChange={() => setSelectedCredentialId(credential.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <img 
                    src={credential.type === 'szamlazz' ? szamlazzLogo : billingoLogo} 
                    alt={credential.type === 'szamlazz' ? 'Számlázz.hu' : 'Billingo'} 
                    className="h-6 w-auto object-contain"
                  />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {credential.name}
                    </h4>
                    {credential.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {credential.description}
                      </p>
                    )}
                  </div>
                  {credential.isActive && (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Kiválasztott credential és szolgáltató összefoglaló */}
      {selectedCredentialId && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
            <span className="text-sm font-medium text-green-800 dark:text-green-200">
              {selectedProvider === 'szamlazz' ? 'Számlázz.hu' : 'Billingo'} credential kiválasztva - Számlák automatikusan kiállításra kerülnek
            </span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Alapértelmezett beállítások */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alapértelmezett fizetési mód
            </label>
            <select
              value={formData.defaultPaymentMethod || 'TRANSFER'}
              onChange={(e) => handleInputChange('defaultPaymentMethod', e.target.value)}
              disabled={!isEditing}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
            >
              <option value="CASH">Készpénz</option>
              <option value="CARD">Bankkártya</option>
              <option value="TRANSFER">Átutalás</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fizetési határidő (nap)
            </label>
            <input
              type="number"
              min="0"
              max="365"
              value={formData.defaultPaymentDeadline || 8}
              onChange={(e) => handleInputChange('defaultPaymentDeadline', parseInt(e.target.value))}
              disabled={!isEditing}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Számla előtag
            </label>
            <input
              type="text"
              value={formData.invoicePrefix || 'SZ-'}
              onChange={(e) => handleInputChange('invoicePrefix', e.target.value)}
              disabled={!isEditing}
              placeholder="SZ-"
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Pénznem
            </label>
            <select
              value={formData.defaultCurrency || 'HUF'}
              onChange={(e) => handleInputChange('defaultCurrency', e.target.value)}
              disabled={!isEditing}
              className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
            >
              <option value="HUF">HUF - Magyar forint</option>
              <option value="EUR">EUR - Euró</option>
              <option value="USD">USD - Amerikai dollár</option>
            </select>
          </div>
        </div>

        {/* Szamlazz.hu specifikus beállítások */}
        {formData.provider === 'szamlazz.hu' && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
            <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">Szamlazz.hu specifikus beállítások</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Számla nyelve
                </label>
                <select
                  value={formData.szamlazzSettings?.invoiceLanguage || 'hu'}
                  onChange={(e) => handleInputChange('szamlazzSettings', {
                    ...formData.szamlazzSettings,
                    invoiceLanguage: e.target.value
                  })}
                  disabled={!isEditing}
                  className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                >
                  <option value="hu">Magyar</option>
                  <option value="en">Angol</option>
                  <option value="de">Német</option>
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.szamlazzSettings?.downloadPdf || false}
                    onChange={(e) => handleInputChange('szamlazzSettings', {
                      ...formData.szamlazzSettings,
                      downloadPdf: e.target.checked
                    })}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">PDF letöltése</span>
                </label>
              </div>
            </div>

            {/* Eladó (kiállító) adatok */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Eladó (kiállító) adatok</h5>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bank neve
                  </label>
                  <input
                    type="text"
                    value={formData.sellerData?.bank || ''}
                    onChange={(e) => handleInputChange('sellerData', {
                      ...formData.sellerData,
                      bank: e.target.value
                    })}
                    disabled={!isEditing}
                    placeholder="pl. OTP Bank"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bankszámlaszám
                  </label>
                  <input
                    type="text"
                    value={formData.sellerData?.bankAccount || ''}
                    onChange={(e) => handleInputChange('sellerData', {
                      ...formData.sellerData,
                      bankAccount: e.target.value
                    })}
                    disabled={!isEditing}
                    placeholder="pl. 11773103-22222222"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Válasz email cím
                  </label>
                  <input
                    type="email"
                    value={formData.sellerData?.replyEmail || ''}
                    onChange={(e) => handleInputChange('sellerData', {
                      ...formData.sellerData,
                      replyEmail: e.target.value
                    })}
                    disabled={!isEditing}
                    placeholder="pl. info@cegem.hu"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email tárgy
                  </label>
                  <input
                    type="text"
                    value={formData.sellerData?.emailSubject || ''}
                    onChange={(e) => handleInputChange('sellerData', {
                      ...formData.sellerData,
                      emailSubject: e.target.value
                    })}
                    disabled={!isEditing}
                    placeholder="Számla"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email szöveg
                  </label>
                  <textarea
                    value={formData.sellerData?.emailText || ''}
                    onChange={(e) => handleInputChange('sellerData', {
                      ...formData.sellerData,
                      emailText: e.target.value
                    })}
                    disabled={!isEditing}
                    placeholder="Köszönjük megrendelését!"
                    rows={3}
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Aláíró neve
                  </label>
                  <input
                    type="text"
                    value={formData.sellerData?.signerName || ''}
                    onChange={(e) => handleInputChange('sellerData', {
                      ...formData.sellerData,
                      signerName: e.target.value
                    })}
                    disabled={!isEditing}
                    placeholder="pl. Vezeték Keresztnév"
                    className="block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Aktív állapot */}
        <div className="mb-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.isActive || false}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              disabled={!isEditing}
              className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Számlázási integráció aktív</span>
          </label>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Ha aktív, akkor az értékesítések után automatikusan kiállításra kerülnek a számlák
          </p>
        </div>

        {/* Gombok */}
        {isEditing && (
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                // Visszaállítjuk az eredeti adatokat
                if (billingSettings) {
                  setFormData(billingSettings);
                }
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Mégse
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md transition-colors"
            >
              {billingSettings ? 'Beállítások mentése' : 'Beállítások létrehozása'}
            </button>
          </div>
        )}
      </form>

      {/* Állapot megjelenítése */}
      {billingSettings && !isEditing && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`h-3 w-3 rounded-full mr-3 ${
                billingSettings.isActive ? 'bg-green-500' : 'bg-gray-400'
              }`}></div>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Állapot: {billingSettings.isActive ? 'Aktív' : 'Inaktív'}
              </span>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Utolsó módosítás: {billingSettings.updatedAt.toLocaleDateString('hu-HU')}
            </div>
          </div>
        </div>
      )}

      {/* Credentials Manager Modal */}
      {showCredentialsManager && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setShowCredentialsManager(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Credentials Manager
                </h2>
                <button
                  onClick={() => setShowCredentialsManager(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <CredentialsManager />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettings;